import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Plus, Minus, Trash2, ArrowRight, ArrowLeft, ShieldCheck, Truck, MapPin, Loader2, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '@/components/shared/Cart';
import { Section, SectionTitle } from '@/components/shared/Section';
import api from '@/lib/api';

const formatCLP = (n) => '$' + new Intl.NumberFormat('es-CL').format(Math.max(0, Math.round(n || 0)));

// Light client-side RUT format check (xx.xxx.xxx-y) — server is the source of truth
const RUT_RE = /^[0-9]{1,3}(?:\.?[0-9]{3}){1,2}-?[0-9kK]$/;

const REGIONES = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', 'O\'Higgins', 'Maule', 'Ñuble',
  'Biobío', 'Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes',
];

export default function MiCotizacionPage() {
  const navigate = useNavigate();
  const { items, removeItem, updateQty, setQty, updateModel, clearCart, totalItems, neto, iva, total, hasRealPrices } = useCart();

  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_name: '',
    company_rut: '',
    delivery_mode: 'retiro',
    delivery_region: '',
    notes: '',
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = 'Mi Cotización · ALLCOM';
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (items.length === 0) { toast.error('Tu cotización está vacía.'); return false; }
    const required = ['contact_name', 'contact_email', 'contact_phone', 'company_name', 'company_rut'];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) { toast.error('Completa todos los datos obligatorios.'); return false; }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) { toast.error('Email corporativo inválido.'); return false; }
    if (!RUT_RE.test(form.company_rut.trim())) { toast.error('Formato de RUT inválido (ej: 77.123.456-7).'); return false; }
    if (form.delivery_mode === 'despacho' && !form.delivery_region) { toast.error('Selecciona la región de despacho.'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const body = {
        ...form,
        delivery_region: form.delivery_mode === 'despacho' ? form.delivery_region : null,
        items: items.map((i) => ({
          slug: i.slug, name: i.name, brand: i.brand, model: i.model,
          qty: i.qty, unit_price: i.unit_price, image: i.image, category: i.category,
        })),
        source_page: '/mi-cotizacion',
        honeypot: '',
      };
      const r = await api.post('/quotes', body);
      setSuccess(r.data);
      clearCart();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No pudimos enviar tu cotización. Reintenta en unos segundos.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Empty state
  if (!success && items.length === 0) {
    return (
      <div className="bg-white">
        <Section>
          <div className="max-w-md mx-auto text-center py-12" data-testid="quote-empty">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-slate-400" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Syne,sans-serif' }}>Tu cotización está vacía</h1>
            <p className="text-sm text-[#64748B] leading-relaxed mb-6">Recorre el catálogo y agrega los equipos que necesitas. Tus selecciones se guardan automáticamente, incluso si cierras el navegador.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/hardware" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-sm" data-testid="quote-empty-browse">
                Ver catálogo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0A2540] bg-white border border-slate-200 rounded-xl hover:bg-slate-50">
                Hablar con un asesor
              </Link>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  // Success state
  if (success) {
    const shortId = String(success.id || '').slice(-8).toUpperCase();
    return (
      <div className="bg-white">
        <Section>
          <div className="max-w-xl mx-auto text-center py-8" data-testid="quote-success">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-600" strokeWidth={2} />
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold mb-2">Pre-cotización enviada</p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#0A2540] mb-3" style={{ fontFamily: 'Syne,sans-serif' }}>#{shortId}</h1>
            <p className="text-sm text-[#64748B] leading-relaxed mb-6">{success.message || 'Tu cotización fue enviada a tu correo y a nuestro equipo de ventas.'}</p>
            {success.totals?.total > 0 && (
              <div className="inline-block px-6 py-4 rounded-xl bg-slate-50 border border-slate-200 text-sm mb-6">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total estimado</div>
                <div className="text-2xl font-bold text-[#0A2540] tabular-nums" style={{ fontFamily: 'Syne,sans-serif' }}>{formatCLP(success.totals.total)}</div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-sm">
                Volver al inicio <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={() => navigate('/hardware')} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0A2540] bg-white border border-slate-200 rounded-xl hover:bg-slate-50">
                Seguir cotizando
              </button>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="mi-cotizacion-page">
      {/* Hero strip — sober, allcom palette */}
      <div className="bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-300 hover:text-white mb-4">
            <ArrowLeft className="w-3 h-3" /> Volver al sitio
          </Link>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#0EA5E9] font-bold mb-2">Plataforma B2B · ALLCOM</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Syne,sans-serif' }}>Mi cotización</h1>
              <p className="text-sm text-slate-300 mt-2 max-w-xl">Configura tu pedido de inversión. Tus selecciones se guardan automáticamente.</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur-sm">
              <ShoppingBag className="w-4 h-4 text-[#0EA5E9]" strokeWidth={1.6} />
              <span className="text-sm font-semibold tabular-nums">{totalItems} equipo{totalItems !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main grid: 70/30 desktop, stacked mobile */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          {/* === Left column: items + form === */}
          <div className="min-w-0 space-y-8">
            {/* Items list (table on desktop, cards on mobile) */}
            <section data-testid="quote-items-section">
              <header className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Syne,sans-serif' }}>Equipos seleccionados</h2>
                <button onClick={() => clearCart()} className="text-[11px] text-slate-500 hover:text-red-500 font-medium" data-testid="quote-clear-btn">Vaciar cotización</button>
              </header>

              {/* Desktop list (CSS grid pretending to be a table — avoids HTML invariants when wrappers inject spans) */}
              <div className="hidden md:block rounded-2xl border border-slate-200 overflow-hidden bg-white">
                {/* Header row */}
                <div className="grid grid-cols-[minmax(0,1fr)_120px_140px_140px_140px_44px] gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <div>Producto</div>
                  <div>Modalidad</div>
                  <div className="text-center">Cantidad</div>
                  <div className="text-right">Precio unitario</div>
                  <div className="text-right">Subtotal</div>
                  <div></div>
                </div>
                {/* Rows */}
                {items.map((it) => {
                  const subtotal = (it.unit_price || 0) * (it.qty || 1);
                  return (
                    <div
                      key={it.slug || it.name}
                      className="grid grid-cols-[minmax(0,1fr)_120px_140px_140px_140px_44px] gap-2 items-start px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 text-sm"
                      data-testid={`quote-row-${it.slug || 'noslug'}`}
                    >
                      {/* Product */}
                      <div className="flex gap-3 items-start min-w-0">
                        {it.image ? (
                          <img src={it.image} alt={it.name} className="w-14 h-14 rounded-lg object-cover bg-slate-50 shrink-0" data-testid={`quote-thumb-${it.slug || 'noslug'}`} />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] text-slate-400 font-semibold shrink-0" data-testid={`quote-thumb-${it.slug || 'noslug'}`}>{(it.brand || 'ALLCOM').slice(0, 8)}</div>
                        )}
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{it.brand || 'ALLCOM'}</div>
                          <div className="text-sm font-semibold text-[#0A2540] leading-tight">{it.name}</div>
                          {it.category && <div className="text-[10px] text-slate-400 mt-0.5">{it.category}</div>}
                        </div>
                      </div>
                      {/* Modalidad */}
                      <div>
                        <select
                          value={it.model}
                          onChange={(e) => updateModel(it.slug, e.target.value)}
                          className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-[#0EA5E9]/30 outline-none"
                          data-testid={`quote-model-${it.slug || 'noslug'}`}
                        >
                          <option value="cotizar">Cotizar</option>
                          <option value="compra">Compra</option>
                          <option value="arriendo">Arriendo</option>
                        </select>
                      </div>
                      {/* Qty */}
                      <div className="flex justify-center">
                        <div className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-1 py-0.5 bg-white">
                          <button onClick={() => updateQty(it.slug, -1)} className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100" aria-label="Disminuir" data-testid={`quote-qty-decrease-${it.slug || 'noslug'}`}>
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <input
                            type="number"
                            value={it.qty}
                            onChange={(e) => setQty(it.slug, e.target.value)}
                            className="w-10 text-center text-sm font-semibold text-[#0A2540] tabular-nums bg-transparent outline-none"
                            min={1} max={999}
                            data-testid={`quote-qty-input-${it.slug || 'noslug'}`}
                          />
                          <button onClick={() => updateQty(it.slug, 1)} className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100" aria-label="Aumentar" data-testid={`quote-qty-increase-${it.slug || 'noslug'}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Unit price */}
                      <div className="text-right tabular-nums">
                        {it.unit_price > 0 ? <span className="text-[#0A2540] font-medium">{formatCLP(it.unit_price)}</span> : <span className="text-[#0EA5E9] font-semibold text-xs">A cotizar</span>}
                      </div>
                      {/* Subtotal */}
                      <div className="text-right tabular-nums">
                        <span className="text-sm font-bold text-[#0A2540]" data-testid={`quote-subtotal-${it.slug || 'noslug'}`}>{subtotal > 0 ? formatCLP(subtotal) : '—'}</span>
                      </div>
                      {/* Remove */}
                      <div className="flex justify-end">
                        <button onClick={() => removeItem(it.slug)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" aria-label="Eliminar" data-testid={`quote-remove-${it.slug || 'noslug'}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {items.map((it) => {
                  const subtotal = (it.unit_price || 0) * (it.qty || 1);
                  return (
                    <div key={it.slug || it.name} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`quote-card-${it.slug || 'noslug'}`}>
                      <div className="flex gap-3 mb-3">
                        {it.image ? (
                          <img src={it.image} alt={it.name} className="w-16 h-16 rounded-lg object-cover bg-slate-50 shrink-0" />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 font-semibold shrink-0">{(it.brand || 'ALLCOM').slice(0, 8)}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{it.brand || 'ALLCOM'}</div>
                          <div className="text-sm font-semibold text-[#0A2540] leading-tight">{it.name}</div>
                          <div className="text-xs text-[#0A2540] mt-1 tabular-nums">{it.unit_price > 0 ? formatCLP(it.unit_price) : <span className="text-[#0EA5E9] font-semibold">A cotizar</span>}</div>
                        </div>
                        <button onClick={() => removeItem(it.slug)} className="p-1 text-slate-400 hover:text-red-500 self-start"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <select value={it.model} onChange={(e) => updateModel(it.slug, e.target.value)} className="text-xs px-2 py-1.5 border border-slate-200 rounded-lg bg-white">
                          <option value="cotizar">Cotizar</option>
                          <option value="compra">Compra</option>
                          <option value="arriendo">Arriendo</option>
                        </select>
                        <div className="inline-flex items-center gap-1 border border-slate-200 rounded-lg px-1 py-0.5 bg-white">
                          <button onClick={() => updateQty(it.slug, -1)} className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100"><Minus className="w-3.5 h-3.5" /></button>
                          <input type="number" value={it.qty} onChange={(e) => setQty(it.slug, e.target.value)} className="w-10 text-center text-sm font-semibold text-[#0A2540] tabular-nums bg-transparent outline-none" min={1} max={999} />
                          <button onClick={() => updateQty(it.slug, 1)} className="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                        <div className="text-sm font-bold text-[#0A2540] tabular-nums">{subtotal > 0 ? formatCLP(subtotal) : '—'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* B2B Form */}
            <section data-testid="quote-form-section">
              <header className="mb-4">
                <h2 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Syne,sans-serif' }}>Datos de facturación</h2>
                <p className="text-xs text-[#64748B] mt-1">Necesitamos estos datos para emitir la pre-cotización formal a tu empresa.</p>
              </header>

              <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-6 rounded-2xl border border-slate-200 bg-white">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Nombre de contacto" required>
                    <input type="text" value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} className={inputCls} placeholder="Nombre y apellido" data-testid="quote-field-contact_name" required />
                  </Field>
                  <Field label="Email corporativo" required>
                    <input type="email" value={form.contact_email} onChange={(e) => update('contact_email', e.target.value)} className={inputCls} placeholder="contacto@empresa.cl" data-testid="quote-field-contact_email" required />
                  </Field>
                  <Field label="Teléfono" required>
                    <input type="tel" value={form.contact_phone} onChange={(e) => update('contact_phone', e.target.value)} className={inputCls} placeholder="+56 9 1234 5678" data-testid="quote-field-contact_phone" required />
                  </Field>
                  <Field label="Razón social" required>
                    <input type="text" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} className={inputCls} placeholder="Empresa SpA / Ltda." data-testid="quote-field-company_name" required />
                  </Field>
                  <Field label="RUT empresa" required hint="Formato: 77.123.456-7">
                    <input type="text" value={form.company_rut} onChange={(e) => update('company_rut', e.target.value)} className={inputCls} placeholder="77.123.456-7" data-testid="quote-field-company_rut" required />
                  </Field>
                  <Field label="Notas (opcional)">
                    <input type="text" value={form.notes} onChange={(e) => update('notes', e.target.value)} className={inputCls} placeholder="Plazo, especificaciones, condiciones…" data-testid="quote-field-notes" />
                  </Field>
                </div>

                {/* Delivery selector */}
                <div>
                  <label className="block text-xs font-semibold text-[#0A2540] uppercase tracking-wider mb-2">Modalidad de entrega <span className="text-red-500">*</span></label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <DeliveryOption
                      icon={MapPin}
                      title="Retiro en oficina"
                      desc="Andrés Bello 1567, Providencia · Lun-Vie 9-18h"
                      checked={form.delivery_mode === 'retiro'}
                      onSelect={() => update('delivery_mode', 'retiro')}
                      testid="quote-delivery-retiro"
                    />
                    <DeliveryOption
                      icon={Truck}
                      title="Despacho a regiones"
                      desc="Transporte privado · Chilexpress · Starken"
                      checked={form.delivery_mode === 'despacho'}
                      onSelect={() => update('delivery_mode', 'despacho')}
                      testid="quote-delivery-despacho"
                    />
                  </div>
                  {form.delivery_mode === 'despacho' && (
                    <div className="mt-3">
                      <select value={form.delivery_region} onChange={(e) => update('delivery_region', e.target.value)} className={inputCls} data-testid="quote-field-delivery_region" required>
                        <option value="">— Selecciona región —</option>
                        {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Conditions block */}
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    <FileText className="w-3.5 h-3.5" />Condiciones comerciales
                  </div>
                  <ul className="space-y-1.5 text-[12px] text-[#475569] leading-relaxed">
                    <li>· Stock sujeto a confirmación. Tiempos de despacho 48-72h hábiles desde aprobación.</li>
                    <li>· Precios en pesos chilenos (CLP) + IVA 19%. Validez 15 días corridos.</li>
                    <li>· Garantía estándar 3 años + soporte técnico 24/7.</li>
                    <li>· Forma de pago: contado · transferencia · OC con plazo a definir.</li>
                  </ul>
                </div>

                <button
                  type="submit"
                  disabled={sending || items.length === 0}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="quote-submit"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  {sending ? 'Enviando…' : `Enviar cotización (${totalItems} ${totalItems === 1 ? 'equipo' : 'equipos'})`}
                </button>
              </form>
            </section>
          </div>

          {/* === Right column: sticky summary === */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="quote-summary">
              <h3 className="text-base font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Syne,sans-serif' }}>Resumen</h3>
              <dl className="space-y-2.5 text-sm">
                <Row label="Productos" value={`${totalItems} ${totalItems === 1 ? 'equipo' : 'equipos'}`} />
                <Row label="Neto" value={hasRealPrices ? formatCLP(neto) : 'Por cotizar'} valueTestid="quote-summary-neto" />
                <Row label="IVA (19%)" value={hasRealPrices ? formatCLP(iva) : '—'} valueTestid="quote-summary-iva" />
                <div className="pt-3 border-t border-slate-200 mt-2 flex items-baseline justify-between gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total estimado</span>
                  <span className="text-2xl font-bold text-[#0A2540] tabular-nums" style={{ fontFamily: 'Syne,sans-serif' }} data-testid="quote-summary-total">{hasRealPrices ? formatCLP(total) : 'Cotizar'}</span>
                </div>
              </dl>

              <div className="mt-5 p-3 rounded-lg bg-sky-50/60 border border-sky-100 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-[#0EA5E9] shrink-0 mt-0.5" strokeWidth={1.6} />
                <p className="text-[11px] text-[#0A2540] leading-relaxed">Garantía 3 años + soporte 24/7 incluido en todos los equipos.</p>
              </div>

              <p className="text-[10px] text-slate-400 mt-4 leading-relaxed">Tus selecciones se guardan automáticamente en este navegador. Puedes cerrar el sitio y volver luego para completarlo.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#0EA5E9]/30 focus:border-[#0EA5E9] outline-none transition-all';

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[#0A2540] uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const DeliveryOption = ({ icon: Icon, title, desc, checked, onSelect, testid }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all ${checked ? 'border-[#0EA5E9] bg-sky-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
    data-testid={testid}
  >
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${checked ? 'bg-[#0EA5E9] text-white' : 'bg-slate-100 text-slate-500'}`}>
      <Icon className="w-4 h-4" strokeWidth={1.6} />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-[#0A2540]">{title}</div>
      <div className="text-[11px] text-[#64748B] leading-relaxed">{desc}</div>
    </div>
    <div className={`w-4 h-4 rounded-full border-2 shrink-0 ml-auto mt-1 ${checked ? 'border-[#0EA5E9] bg-[#0EA5E9]' : 'border-slate-300 bg-white'}`}>
      {checked && <div className="w-full h-full rounded-full bg-white scale-[0.4]" />}
    </div>
  </button>
);

const Row = ({ label, value, valueTestid }) => (
  <div className="flex items-baseline justify-between gap-3">
    <dt className="text-[#64748B]">{label}</dt>
    <dd className="font-medium text-[#0A2540] tabular-nums" data-testid={valueTestid}>{value}</dd>
  </div>
);
